import React from 'react';
import Button from 'atoms/Button/Button';
import Typography from 'atoms/Typography/Typography';
import { MarkupElement, MarkupValue } from 'src/api/models/MarkupHtml';

interface ParagraphHtmlComponentLink {
  type: 'link';
  href: string;
  text: string;
}

interface ParagraphHtmlComponentText {
  type: 'text';
  text: string;
}

interface ParagraphHtmlComponentSpacing {
  type: 'spacing';
  text: null;
}

type ParagraphHtmlComponentChild =
  | ParagraphHtmlComponentLink
  | ParagraphHtmlComponentText
  | ParagraphHtmlComponentSpacing;

export interface ParagraphHtmlComponents {
  type: 'tag';
  tagName: 'p';
  children: ParagraphHtmlComponentChild[];
}

const paragraphElementToStructuredData = (markup: MarkupElement): ParagraphHtmlComponentChild => {
  if (markup.tagName === 'br') {
    return { type: 'spacing', text: null } as ParagraphHtmlComponentSpacing;
  }

  if (markup.tagName === 'a') {
    return {
      type: 'link',
      href: markup.properties.href,
      text: markup.children.map((c) => (c.type === 'text' ? c.value : '')).join(''),
    } as ParagraphHtmlComponentLink;
  }

  return null;
};

const paragraphTextToStructuredData = (markup: MarkupValue): ParagraphHtmlComponentText => {
  if (markup.value !== '\n') {
    return { type: 'text', text: markup.value } as ParagraphHtmlComponentText;
  }

  return null;
};

export const structuredParagraphHtmlComponents = (htmlAst: MarkupElement): ParagraphHtmlComponents => {
  if (htmlAst.tagName !== 'p') return null;

  const hasImageChild = (htmlAst.children as unknown as MarkupElement[])
    .filter((c) => c.type === 'element')
    .some((c: MarkupElement) => c.tagName === 'img');
  if (hasImageChild) return null;

  // Attachments are single children of a p tag
  const isAttachment = (htmlAst.children as unknown as MarkupValue[])
    .filter((c) => c.type === 'text')
    .some((c: MarkupValue) => !!c.value?.match(/\(\(([toc)]+)\)\)/g) || !!c.value?.match(/\(\(([0-9)]+)\)\)/g));
  if (isAttachment) return null;

  // Reformatting children content for paragraph components
  const children = htmlAst.children
    .map((c) => {
      if (c.type === 'element') {
        return paragraphElementToStructuredData(c);
      }

      if (c.type === 'text') {
        return paragraphTextToStructuredData(c);
      }

      return null;
    })
    .filter((c) => !!c);

  return { type: 'tag', tagName: 'p', children };
};

interface ParagraphHtmlComponentProps {
  structuredHtml: ParagraphHtmlComponents;
  key: string;
}

const ParagraphHtmlComponent = ({ structuredHtml, key }: ParagraphHtmlComponentProps): JSX.Element => {
  return (
    <Typography
      type='paragraph'
      size='md'
      className='ast-p-element'
      key={key}
    >
      {structuredHtml.children.map((c, index) => {
        if (c.type === 'text') return <span key={`${key}--${index}`}>{c.text}</span>;

        if (c.type === 'link')
          return (
            <Button
              href={c.href}
              key={`${key}--${index}`}
              className='ast-link-element'
              variant='brand-text-link'
              display='inline'
              decoration='underline'
              size='lg'
            >
              {c.text}
            </Button>
          );

        if (c.type === 'spacing') return <br key={`${key}--${index}`} />;

        return null;
      })}
    </Typography>
  );
};

export default ParagraphHtmlComponent;
